import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMatterTagV1,
  deleteMatterTagV1,
  updateMatterTagV1,
} from "@/lib/data/requests";
import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateMatterTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTagV1>[0]) =>
      createMatterTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              tags: orderBy(
                [
                  ...oldData.data.tags,
                  { dateCreated: dayjs().toISOString(), ...data },
                ],
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter tag created successfully");
    },
  });
};

export const useUpdateMatterTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTagV1>[0]) =>
      updateMatterTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              tags: oldData.data.tags.map((tag) =>
                tag.id === data.id ? { ...tag, ...data } : tag,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter tag updated successfully");
    },
  });
};

export const useDeleteMatterTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTagV1>[0]) =>
      deleteMatterTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              tags: oldData.data.tags.filter((tag) => tag.id !== data.id),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toast.error("Error deleting matter tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter tag deleted successfully");
    },
  });
};
